import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import Cropper from 'react-easy-crop';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to create image from URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

// Helper function to get cropped image
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
}

export default function MentorDetailPage() {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const [mentorData, setMentorData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sessions');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now());
  const { getToken } = useAdminAuth();

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Edit form
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchMentorDetail();
    fetchAvailableSessions();
  }, [mentorId]);

  const fetchMentorDetail = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/mentors/${mentorId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setMentorData(response.data);
      setEditForm({
        full_name: response.data.mentor.full_name,
        email: response.data.mentor.email,
        bio: response.data.mentor.bio || '',
        specialties: response.data.mentor.specialties?.join(', ') || '',
        status: response.data.mentor.status
      });
    } catch (error) {
      console.error('Error fetching mentor detail:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableSessions = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/sessions`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      // Show ALL sessions (not just unassigned ones)
      // This allows reassigning sessions between mentors
      setAvailableSessions(response.data.sessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const handleUpdateMentor = async (e) => {
    e.preventDefault();
    try {
      const specialtiesArray = editForm.specialties.split(',').map(s => s.trim()).filter(s => s);
      await axios.patch(
        `${BACKEND_URL}/api/admin/mentors/${mentorId}`,
        { ...editForm, specialties: specialtiesArray },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Mentor actualizado exitosamente');
      setShowEditModal(false);
      fetchMentorDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar mentor');
    }
  };

  const handleAssignSession = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/mentors/${mentorId}/assign-session`,
        { session_id: selectedSession },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Sesión asignada exitosamente');
      setShowAssignModal(false);
      setSelectedSession('');
      fetchMentorDetail();
      fetchAvailableSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar sesión');
    }
  };

  const handleUnassignSession = async (sessionId) => {
    if (!window.confirm('¿Desasignar esta sesión del mentor?')) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/mentors/${mentorId}/sessions/${sessionId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Sesión desasignada');
      fetchMentorDetail();
      fetchAvailableSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    // Validate file size (10MB for editing)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 10MB');
      return;
    }

    // Read file and show crop modal
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowPhotoModal(false);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async () => {
    if (!croppedAreaPixels || !imageSrc) return;

    setIsUploadingPhoto(true);
    try {
      // Get cropped image blob
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Create FormData with cropped image
      const formData = new FormData();
      formData.append('file', croppedBlob, 'avatar.jpg');

      await axios.post(
        `${BACKEND_URL}/api/admin/mentors/${mentorId}/upload-avatar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      toast.success('✅ Foto actualizada exitosamente', {
        position: "top-center",
        autoClose: 2000,
      });
      setShowCropModal(false);
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setImageKey(Date.now()); // Force image reload
      await fetchMentorDetail();
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setShowPhotoModal(true);
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('¿Estás seguro de eliminar la foto del mentor?')) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/mentors/${mentorId}/avatar`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Foto eliminada exitosamente');
      setShowPhotoModal(false);
      setImageKey(Date.now()); // Force image reload
      await fetchMentorDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la foto');
    }
  };

  if (isLoading || !mentorData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  const { mentor, sessions, stats } = mentorData;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <button onClick={() => navigate('/admin/dashboard')} className="hover:text-[#D4AF37]">Admin</button>
        <span>/</span>
        <button onClick={() => navigate('/admin/mentors')} className="hover:text-[#D4AF37]">Mentores</button>
        <span>/</span>
        <span className="text-white">{mentor.full_name}</span>
      </div>

      {/* Header */}
      <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="relative group">
              {mentor.avatar_url ? (
                <>
                  <img
                    key={imageKey}
                    src={mentor.avatar_url}
                    alt={mentor.full_name}
                    className="w-20 h-20 rounded-full object-cover border-2 border-[#D4AF37]"
                    onError={(e) => {
                      console.error('Error loading avatar:', mentor.avatar_url);
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E4B7] items-center justify-center text-black font-bold text-2xl hidden">
                    {mentor.full_name.charAt(0).toUpperCase()}
                  </div>
                </>
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E4B7] flex items-center justify-center text-black font-bold text-2xl">
                  {mentor.full_name.charAt(0).toUpperCase()}
                </div>
              )}
              <button
                onClick={() => setShowPhotoModal(true)}
                className="absolute inset-0 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{mentor.full_name}</h1>
              <p className="text-gray-400">{mentor.email}</p>
            </div>
          </div>
          <button
            onClick={() => setShowEditModal(true)}
            className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F4E4B7]"
          >
            Editar
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Total Sesiones</div>
            <div className="text-2xl font-bold text-white">{stats.total_sessions}</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Próximas</div>
            <div className="text-2xl font-bold text-yellow-400">{stats.upcoming_sessions}</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Completadas</div>
            <div className="text-2xl font-bold text-green-400">{stats.completed_sessions}</div>
          </div>
        </div>

        {mentor.bio && <p className="mt-4 text-gray-300">{mentor.bio}</p>}

        {mentor.specialties && mentor.specialties.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {mentor.specialties.map((s, i) => (
              <span key={i} className="px-3 py-1 bg-[#1a4d5c]/30 text-[#D4AF37] text-sm rounded">{s}</span>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1a4d5c]/30">
        <div className="flex border-b border-[#1a4d5c]/30">
          <button
            onClick={() => setActiveTab('sessions')}
            className={`px-6 py-3 font-medium ${activeTab === 'sessions' ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-gray-400'}`}
          >
            Sesiones ({sessions.length})
          </button>
        </div>

        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-white">Sesiones Asignadas</h3>
            <button
              onClick={() => setShowAssignModal(true)}
              className="px-4 py-2 bg-[#D4AF37] text-black rounded hover:bg-[#F4E4B7]"
            >
              + Asignar Sesión
            </button>
          </div>

          {sessions.length > 0 ? (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div key={session.id} className="bg-black/30 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <h4 className="text-white font-bold">{session.title}</h4>
                    <p className="text-sm text-gray-400">{session.pilar} • {new Date(session.date).toLocaleDateString('es-ES')}</p>
                  </div>
                  <button
                    onClick={() => handleUnassignSession(session.id)}
                    className="px-3 py-1 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20"
                  >
                    Desasignar
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">No hay sesiones asignadas</p>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] rounded-lg max-w-2xl w-full border border-[#1a4d5c]/30 p-6">
            <h3 className="text-2xl font-bold text-white mb-4">Editar Mentor</h3>
            <form onSubmit={handleUpdateMentor} className="space-y-4">
              <input
                type="text"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
                placeholder="Nombre"
              />
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
                placeholder="Email"
              />
              <textarea
                value={editForm.bio}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                rows="3"
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
                placeholder="Bio"
              />
              <input
                type="text"
                value={editForm.specialties}
                onChange={(e) => setEditForm({ ...editForm, specialties: e.target.value })}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
                placeholder="Especialidades (separadas por coma)"
              />
              
              {/* Cambiar Foto Section */}
              <div className="border-t border-[#1a4d5c]/30 pt-4">
                <label className="block text-sm text-gray-400 mb-2">Cambiar Foto del Mentor</label>
                <div className="flex items-center gap-4">
                  {mentor.avatar_url && (
                    <img
                      src={mentor.avatar_url}
                      alt="Current avatar"
                      className="w-16 h-16 rounded-full object-cover border-2 border-[#D4AF37]"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setShowPhotoModal(true)}
                    className="px-4 py-2 bg-[#1a4d5c]/30 text-[#D4AF37] rounded-lg hover:bg-[#1a4d5c]/50 transition-colors text-sm"
                  >
                    📷 {mentor.avatar_url ? 'Cambiar Foto' : 'Agregar Foto'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Haz click para subir y recortar una nueva foto</p>
              </div>
              
              <div className="flex gap-3">
                <button type="submit" className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg">Guardar</button>
                <button type="button" onClick={() => setShowEditModal(false)} className="px-6 py-3 bg-gray-700 text-white rounded-lg">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Session Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] rounded-lg max-w-lg w-full border border-[#1a4d5c]/30 p-6">
            <h3 className="text-2xl font-bold text-white mb-4">Asignar Sesión</h3>
            <form onSubmit={handleAssignSession} className="space-y-4">
              <select
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white"
                required
              >
                <option value="">Selecciona una sesión</option>
                {availableSessions.map((s) => (
                  <option key={s.id} value={s.id}>{s.title} - {new Date(s.date).toLocaleDateString('es-ES')}</option>
                ))}
              </select>
              <div className="flex gap-3">
                <button type="submit" className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg">Asignar</button>
                <button type="button" onClick={() => setShowAssignModal(false)} className="px-6 py-3 bg-gray-700 text-white rounded-lg">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full border border-[#1a4d5c]/30 p-6">
            <h3 className="text-2xl font-bold text-white mb-4">Foto del Mentor</h3>
            
            {mentor.avatar_url && (
              <div className="mb-4">
                <img
                  key={imageKey}
                  src={mentor.avatar_url}
                  alt={mentor.full_name}
                  className="w-32 h-32 rounded-full object-cover mx-auto border-2 border-[#D4AF37]"
                />
              </div>
            )}

            <div className="space-y-3">
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  disabled={isUploadingPhoto}
                  className="hidden"
                  id="photo-upload"
                />
                <span className="block w-full px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors text-center cursor-pointer">
                  {mentor.avatar_url ? 'Cambiar Foto' : 'Subir Foto'}
                </span>
              </label>

              {mentor.avatar_url && (
                <button
                  onClick={handleDeletePhoto}
                  disabled={isUploadingPhoto}
                  className="w-full px-6 py-3 bg-red-500/10 text-red-400 font-medium rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
                >
                  Eliminar Foto
                </button>
              )}

              <button
                onClick={() => setShowPhotoModal(false)}
                disabled={isUploadingPhoto}
                className="w-full px-6 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cerrar
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">
              Formatos aceptados: JPG, PNG, WebP. Máximo 10MB.
            </p>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-3xl">
            <div className="bg-[#0A0A0A] rounded-lg border border-[#1a4d5c]/30 overflow-hidden">
              <div className="p-4 border-b border-[#1a4d5c]/30">
                <h3 className="text-2xl font-bold text-white">Ajustar Foto</h3>
                <p className="text-sm text-gray-400 mt-1">Arrastra para mover, usa el slider para hacer zoom</p>
              </div>

              {/* Cropper Area */}
              <div className="relative h-96 bg-black">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              {/* Controls */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Zoom: {zoom.toFixed(1)}x
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#D4AF37]"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleCropSave}
                    disabled={isUploadingPhoto}
                    className="flex-1 px-6 py-3 bg-[#D4AF37] text-black font-bold rounded-lg hover:bg-[#F4E4B7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploadingPhoto ? 'Subiendo...' : 'Guardar Foto'}
                  </button>
                  <button
                    onClick={handleCropCancel}
                    disabled={isUploadingPhoto}
                    className="px-6 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
